'use client';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { useEffect, type PropsWithChildren, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@eliadmin/core/src/components/ui/popover';
import { usePathname } from 'next/navigation';
import { IoIosArrowDown } from 'react-icons/io';
import type { MenuField } from './mobile-sidebar';

export type CategoryMenuProps = {
  className?: string;
};

export function CategoryMenu(props: PropsWithChildren<CategoryMenuProps>) {
  return (
    <ul className={cn('flex flex-row items-center gap-2', 'ea-category-menu', props.className)}>
      {props.children}
    </ul>
  );
}

export type CategoryMenuItemProps = {
  title: string;
  href?: string;
  subpages?: MenuField[];
  popover?: boolean;
  onClick?: () => void;
  order?: number;
  className?: {
    root?: string;
    link?: string;
  };
};

export function CategoryMenuItem(props: CategoryMenuItemProps) {
  const LinkComp = props.href ? (isRelativeHref(getSafeHref(props.href)) ? Link : 'a') : 'span';
  const currentUrl = usePathname();

  const [open, setOpen] = useState(false);

  const handleMouseEnter = () => {
    setOpen(true);
  };

  const handleMouseLeave = () => {
    setOpen(false);
  };

  useEffect(() => {
    const container = document.querySelector('.ea-header-main') as HTMLElement;
    if (!container) return;

    if (currentUrl === '/') {
      container.style.backgroundColor = 'white';
    } else {
      container.style.backgroundColor = '#EBECFF';
    }

    return () => {
      container.style.backgroundColor = '';
    };
  }, [currentUrl]);

  return (
    <>
      {props.subpages && props.popover ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            onMouseEnter={handleMouseEnter}
            className={cn(
              'relative flex items-center justify-center gap-x-3 font-semibold text-white',
              currentUrl.includes(props.href!) ? 'text-primary' : 'text-[#565656]',
            )}
          >
            <span> {props.title}</span>
            <IoIosArrowDown
              className={cn(
                'text-secondary transition-all duration-300',
                open ? 'rotate-180' : 'rotate-0',
              )}
            />
          </PopoverTrigger>
          <PopoverContent
            onMouseLeave={handleMouseLeave}
            className={cn(
              'relative left-[25%] rounded-none border-none bg-white p-0 shadow-lg',
              'ea-popover-content',
            )}
          >
            <div className="flex flex-col items-start justify-start">
              {props.subpages.map((item, i) => {
                return (
                  item.href && (
                    <Link prefetchOnHover
                      key={i}
                      className={cn(
                        'w-full bg-white p-5 text-[#565656] transition-colors hover:bg-primary/90 hover:text-white',
                      )}
                      passHref
                      href={`/${item.href}`}
                    >
                      <span className="font-medium ">{item.name}</span>
                    </Link>
                  )
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <li
          style={{ order: props.order }}
          className={cn(
            'flex flex-row',
            {
              'cursor-pointer': !!props.href,
              'cursor-default': !props.href,
            },
            'ea-category-menu-item',

            props.className?.root,
          )}
        >
          <LinkComp {...(LinkComp === Link ? { prefetchOnHover: true } : {})}
            href={props.href ? getSafeHref(props.href) : (undefined as any)}
            className={cn(
              'relative text-[16px] font-semibold text-[#565656]',
              'ea-category-menu-item__link',
              props.className?.link,
              getSafeHref(props.href!) === currentUrl ? 'text-primary' : 'text-[#565656]',
            )}
            onClick={props.onClick}
          >
            {props.title}
          </LinkComp>
        </li>
      )}
    </>
  );
}
